import { apiGet, type PageResult } from '~/utils/api'

export interface BlogTagSummary {
  slug: string
  name: string
}

export interface BlogCategorySummary {
  slug: string
  name: string
}

export interface BlogArticleListItem {
  id: string
  slug: string
  title: string
  excerpt?: string
  coverImageUrl?: string
  authorName: string
  readingMinutes?: number
  publishedAt?: string
  category?: BlogCategorySummary
  tags: BlogTagSummary[]
}

export interface BlogArticle extends BlogArticleListItem {
  content: string
  ogImageUrl?: string
  metaTitle?: string
  metaDescription?: string
  canonicalUrl?: string
  updatedAt?: string
  authorProfileUrl?: string
  factStatus: 'unreviewed' | 'verified' | 'reported' | 'rumor' | 'opinion'
  reviewedBy?: string
  sourceUrls: string[]
}

export interface BlogCategoryItem {
  slug: string
  name: string
  description?: string
  articleCount: number
}

export interface BlogTagItem {
  slug: string
  name: string
  articleCount: number
}

export function useBlog() {
  const { locale } = useI18n()

  async function fetchArticles(opts: { category?: string; tag?: string; page?: number; size?: number } = {}): Promise<PageResult<BlogArticleListItem>> {
    const params = new URLSearchParams()
    if (opts.category) params.set('category', opts.category)
    if (opts.tag) params.set('tag', opts.tag)
    params.set('page', String(opts.page ?? 0))
    params.set('size', String(opts.size ?? 12))
    params.set('locale', locale.value)
    return await apiGet<PageResult<BlogArticleListItem>>(`/api/app/v1/blog/articles?${params}`)
  }

  async function fetchArticle(slug: string): Promise<BlogArticle> {
    return await apiGet<BlogArticle>(`/api/app/v1/blog/articles/${slug}?locale=${locale.value}`)
  }

  async function fetchPreview(id: string, token: string): Promise<BlogArticle> {
    return await apiGet<BlogArticle>(`/api/app/v1/blog/articles/preview/${id}?token=${encodeURIComponent(token)}&locale=${locale.value}`)
  }

  async function fetchRelated(id: string, limit = 3): Promise<BlogArticleListItem[]> {
    return await apiGet<BlogArticleListItem[]>(`/api/app/v1/blog/articles/${id}/related?limit=${limit}&locale=${locale.value}`)
  }

  async function fetchCategories(): Promise<BlogCategoryItem[]> {
    return await apiGet<BlogCategoryItem[]>(`/api/app/v1/blog/categories?locale=${locale.value}`)
  }

  async function fetchTags(): Promise<BlogTagItem[]> {
    return await apiGet<BlogTagItem[]>(`/api/app/v1/blog/tags?locale=${locale.value}`)
  }

  return {
    fetchArticles,
    fetchArticle,
    fetchPreview,
    fetchRelated,
    fetchCategories,
    fetchTags,
  }
}
